/**
 * SmsComposer — champs communs d'un texto manuel (Batch #14.336) : destinataire (modifiable), texte (modifiable, validé
 * par l'admin avant l'envoi), compteur caractères / segments / encodage, liens inclus. Utilisé par SmsSendModal et SmsOptIn.
 */
import { Smartphone } from "lucide-react";

const GSM7 = new Set("@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !\"#¤%&'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà^{}\\[~]|€".split(""));
const GSM7_EXT = new Set("^{}\\[~]|€".split(""));

export function smsSegments(text) {
  const t = text || "";
  const ucs2 = [...t].some((ch) => !GSM7.has(ch));
  if (ucs2) { const n = [...t].length; return { chars: n, segments: n === 0 ? 0 : n <= 70 ? 1 : Math.ceil(n / 67), encoding: "UCS-2" }; }
  const n = [...t].reduce((a, ch) => a + (GSM7_EXT.has(ch) ? 2 : 1), 0);
  return { chars: n, segments: n === 0 ? 0 : n <= 160 ? 1 : Math.ceil(n / 153), encoding: "GSM-7" };
}

export function SmsCounter({ text, costPerUnit = 0.1, testId = "sms-counter" }) {
  const s = smsSegments(text);
  const warn = s.segments > 3;
  return (
    <div className={`text-[11px] flex flex-wrap gap-x-3 ${warn ? "text-amber-700" : "text-slate-500"}`} data-testid={testId}>
      <span>{s.chars} caractère{s.chars > 1 ? "s" : ""}</span>
      <span>{s.segments} segment{s.segments > 1 ? "s" : ""} ({s.encoding})</span>
      <span>≈ {(s.segments * costPerUnit).toFixed(2)} $</span>
      {s.encoding === "UCS-2" && <span title="Un caractère hors alphabet GSM (ex. ê, ç, œ, « ») réduit chaque segment à 70 caractères">accents étendus</span>}
    </div>
  );
}

export default function SmsComposer({ to, onTo, text, onText, links, phoneMissing, clientName, idPrefix = "sms" }) {
  const required = Object.values(links || {}).filter(Boolean);
  const missingLink = required.length > 0 && required.some((l) => !(text || "").includes(l));
  return (
    <div className="space-y-2" data-testid={`${idPrefix}-composer`}>
      <div>
        <label className="text-xs font-semibold text-slate-600 flex items-center gap-1"><Smartphone className="w-3.5 h-3.5" /> Numéro de cellulaire{clientName ? ` — ${clientName}` : ""}</label>
        <input value={to} onChange={(e) => onTo(e.target.value)} placeholder="+1 418 555-0123" inputMode="tel"
          className={`mt-1 w-full border rounded-lg px-3 py-2 text-sm font-mono ${!to ? "border-rose-300 bg-rose-50/40" : "border-slate-300"}`} data-testid={`${idPrefix}-to`} />
        {phoneMissing && !to && <p className="text-[11px] text-rose-700 mt-1" data-testid={`${idPrefix}-phone-missing`}>Aucun numéro au dossier client — entrez-le ici (il ne sera pas enregistré dans la fiche).</p>}
      </div>
      <div>
        <label className="text-xs font-semibold text-slate-600">Texte du texto (modifiable)</label>
        <textarea value={text} onChange={(e) => onText(e.target.value)} rows={5} className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm leading-snug" data-testid={`${idPrefix}-text`} />
        <SmsCounter text={text} testId={`${idPrefix}-counter`} />
        {missingLink && <p className="text-[11px] text-amber-700 mt-1" data-testid={`${idPrefix}-link-warning`}>Attention : un lien a été retiré du texte{required.includes("{lien}") ? " — il sera ajouté à la fin au moment de l'envoi" : " — le client ne pourra pas ouvrir le document ou payer"}.</p>}
      </div>
      {links && Object.values(links).some(Boolean) && (
        <div className="text-[11px] text-slate-500 space-y-0.5" data-testid={`${idPrefix}-links`}>
          {links.pdf && <div>Facture (PDF public) : <span className="font-mono break-all">{links.pdf}</span></div>}
          {links.pay && <div>Paiement en ligne : <span className="font-mono break-all">{links.pay}</span></div>}
          {links.link && links.link !== "{lien}" && <div>Lien : <span className="font-mono break-all">{links.link}</span></div>}
          <div className="text-slate-400">Un texto ne transporte jamais la pièce jointe : seulement le lien.</div>
        </div>
      )}
    </div>
  );
}
